/**
 * Localization for the prototype — reuses the production `react-localization`
 * instance held in Redux (`state.localizations.l`), so it shares the live app's
 * translations, language-resolution and the 15 bundled languages with no new
 * dependency. Prototype-specific copy lives under the `resalesLanding` namespace
 * in `src/localizations/*.js` (English is canonical; missing languages fall back
 * to English automatically via react-localization's `_fallbackValues`).
 *
 * Language is resolved once on load in `useOffers` (see below), matching the
 * production precedence: URL `?lang=` → API `language` → cookie → default (en).
 */
import { useSelector } from 'react-redux';
import actions from '../../actions';

/* Returns the active `resalesLanding` string table. Subscribes to
   `currentLanguage` as well so the subtree re-renders when the language changes
   — the localization instance is mutated in place, so selecting `l` alone would
   keep the same reference and miss the update. */
export function useStrings() {
    // subscription side-effect: re-render when the language changes
    useSelector((s) => s && s.localizations && s.localizations.currentLanguage);
    const l = useSelector((s) => s && s.localizations && s.localizations.l);
    return (l && l.resalesLanding) || {};
}

/* The FULL react-localization instance — needed for top-level keys that live
   outside the `resalesLanding` namespace (propTitle / searchType / newDevelopment)
   and for `l.formatString`. Subscribes to currentLanguage like useStrings. */
export function useLocalization() {
    useSelector((s) => s && s.localizations && s.localizations.currentLanguage);
    return useSelector((s) => s && s.localizations && s.localizations.l);
}

/* Builds the property title split into three display lines, EXACTLY matching the
   production app's single-line title (DetailHeader.js / PropertyDetails.js):
     line 0 = property type   ("Villa", or "New Development")
     line 1 = connector       ("for sale in" / "en venta en") — derived from
              `propTitle` with the type + location tokens blanked out, so it stays
              translated per language
     line 2 = location        ("Marbella")
   The raw pieces come from `offer.titleParts` (subType, searchType code,
   location). When the localization instance / keys aren't available it falls back
   to the prebuilt English `offer.title` in line 0 (the other lines empty).
   `propertyTitle` joins these back into the original one-line string. */
export function propertyTitleLines(l, offer) {
    const p = (offer && offer.titleParts) || null;
    if (!p || !l || typeof l.formatString !== 'function' || !l.propTitle) {
        return [(offer && offer.title) || '', '', ''];
    }
    const type = p.isDevelopment ? l.newDevelopment : (p.subType || '');
    const searchType = (l.searchType && l.searchType[p.searchType]) || '';
    const connector = l.formatString(l.propTitle, { subType: '', searchType, location: '' })
        .replace(/\s+/g, ' ').trim();
    return [type, connector, p.location || ''];
}

/* The same title as one line ("Villa for sale in Marbella"), for callers that
   don't need the split (e.g. the contact dialog, mobile title card). */
export function propertyTitle(l, offer) {
    return propertyTitleLines(l, offer).filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

/* Resolve + dispatch the language for the prototype, mirroring production
   precedence. `paramLang` (URL ?lang=) wins, then the API `language`; if neither
   is given the reducer's cookie/default already applies, so we dispatch nothing.
   Best-effort: any failure is swallowed so localization can never break the
   caller (e.g. it must never turn a successful data fetch into a mock fallback). */
export function applyLanguage(dispatch, { paramLang, apiLang } = {}) {
    const lang = paramLang || apiLang;
    if (!lang) return;
    try {
        dispatch(actions.loadLocalization(lang));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[resales-landing] could not apply language:', lang, e);
    }
}

/* Some feature values are bare qualifiers that don't read clearly on their own
   ("Private", "Heated", "Covered", "Fully Fitted", "Excellent"). For the
   categories below we append/prepend the (localized) category name so the value
   is self-explanatory — e.g. EN "Heated Pool", ES "Piscina climatizada".
   Order and wording come from the language file (strings.featureQualifierFormat +
   strings.featureCategoryLabels), so it adapts per language. Values that already
   mention the category (e.g. "Sea Views", "Private Pool", "Garage") are left
   untouched. `category` is the raw feed category name (English). */
const FEATURE_QUALIFY = {
    views:     ['view'],
    pool:      ['pool'],
    parking:   ['parking', 'garage'],
    kitchen:   ['kitchen'],
    condition: ['condition'],
    garden:    ['garden'],
};

export function featureLabel(strings, category, value) {
    const v = String(value == null ? '' : value);
    const cat = String(category || '').toLowerCase();
    const key = Object.keys(FEATURE_QUALIFY).find((k) => cat === k || cat.indexOf(k) !== -1);
    if (!key) return v;
    const lv = v.toLowerCase();
    // already references the category (or a synonym) → already clear, leave as-is
    if (FEATURE_QUALIFY[key].some((word) => lv.indexOf(word) !== -1)) return v;
    const labels = (strings && strings.featureCategoryLabels) || {};
    const label = labels[key];
    if (!label) return v; // no localized label available → don't guess
    const tpl = (strings && strings.featureQualifierFormat) || '{value} {category}';
    return tpl.replace('{value}', v).replace('{category}', label).replace(/\s+/g, ' ').trim();
}
